/**
 * Ficha de uma estação de nível.
 *
 * A regra desta tela: **o número nunca aparece sozinho**. "20,69 m" não diz
 * nada — pode ser cheia histórica ou seca recorde, dependendo do rio e da
 * época. Ao lado dele vem sempre a comparação com os anos medidos, que é o
 * que transforma a medição em informação.
 *
 * E o rótulo é "cota (régua local)", nunca "altura" ou "profundidade": o zero
 * da régua é uma referência local arbitrária. "Óbidos 3,19 m" num rio com
 * dezenas de metros de fundo é uma frase que se lê errado com facilidade.
 */
import { useMemo } from 'react';
import { formatLonLat } from '../lib/format';
import { formatDateBrt, formatDayTimeBrt } from '../lib/time';
import { useTimeStore } from '../state/time';
import { GRAFICO_A, GraficoTempo } from '../ui/GraficoTempo';
import { centroPct } from '../ui/fatias';
import { COR_CLASSE, COR_SEM_REFERENCIA, COR_TENDENCIA } from './look';
import {
  NOME_CLASSE,
  NOME_TENDENCIA,
  SETA_TENDENCIA,
  anomaliaTexto,
  cotaM,
  escalaDaSerie,
  fraseComparativa,
  indiceNoQuadro,
  trechosDaSerie,
  ultimaMedicao,
  variacaoTexto,
  vazaoTexto,
} from './nivel';
import { useRiverStore } from './store';
import type { RioFeature } from './layer';
import { ProximosDiasAqui } from '../previsao/ProximosDiasAqui';

/**
 * Série de 48 h, na mesma moldura do gráfico de chuva: eixo de tempo, marca do
 * instante que acompanha o player e leitura da cota sob o mouse.
 */
function Serie(props: {
  serie: readonly (number | null)[];
  t0: number;
  passoMin: number;
  instante: number | null;
  cor: string;
}) {
  const { serie, t0, passoMin, cor } = props;
  const escala = useMemo(() => escalaDaSerie(serie), [serie]);
  const trechos = useMemo(() => trechosDaSerie(serie), [serie]);
  if (!escala) return null;

  const n = serie.length;
  const A = GRAFICO_A;
  const FOLGA = 2.5;
  const y = (cm: number) => A - FOLGA - ((cm - escala.base) / escala.amplitude) * (A - 2 * FOLGA);
  const medida = (i: number | null) => (i === null ? null : (serie[i] ?? null));

  return (
    <GraficoTempo
      instantes={serie.map((_, i) => t0 + i * passoMin * 60_000)}
      iAtual={indiceNoQuadro(n, t0, passoMin, props.instante)}
      rotulo={`Cota nas últimas 48 horas, entre ${cotaM(escala.min)} e ${cotaM(escala.max)} metros`}
      valor={(i) => {
        const cm = medida(i);
        return cm === null ? null : `${cotaM(cm)} m`;
      }}
      resumo={`${cotaM(escala.min)}–${cotaM(escala.max)} m`}
      desenho={({ w, sob }) => (
        <>
          {/* Guia do hover; sem mouse quem marca a hora é a linha do quadro. */}
          {sob !== null && (
            <line
              x1={(sob + 0.5) * w}
              y1="0"
              x2={(sob + 0.5) * w}
              y2={A}
              stroke="rgba(255,255,255,0.35)"
              strokeWidth="1"
              vectorEffect="non-scaling-stroke"
            />
          )}
          {trechos.map((t) =>
            t.length === 1 ? (
              // Medição isolada entre buracos: um traço curto, para não sumir.
              <line
                key={t[0]}
                x1={(t[0]! + 0.2) * w}
                x2={(t[0]! + 0.8) * w}
                y1={y(serie[t[0]!]!)}
                y2={y(serie[t[0]!]!)}
                stroke={cor}
                strokeWidth="1.5"
                strokeLinecap="round"
                vectorEffect="non-scaling-stroke"
              />
            ) : (
              <polyline
                key={t[0]}
                points={t.map((i) => `${((i + 0.5) * w).toFixed(2)},${y(serie[i]!).toFixed(2)}`).join(' ')}
                fill="none"
                stroke={cor}
                strokeWidth="1.5"
                strokeLinejoin="round"
                strokeLinecap="round"
                vectorEffect="non-scaling-stroke"
              />
            ),
          )}
        </>
      )}
      sobre={({ destaque }) => {
        const cm = medida(destaque);
        if (destaque === null || cm === null) return null;
        // Em HTML, e não no SVG: o `preserveAspectRatio="none"` faria do ponto uma elipse.
        return (
          <span
            aria-hidden
            className="pointer-events-none absolute size-[7px] -translate-x-1/2 -translate-y-1/2 rounded-full ring-2 ring-ink-900"
            style={{ left: `${centroPct(destaque, n)}%`, top: `${(y(cm) / A) * 100}%`, background: cor }}
          />
        );
      }}
    />
  );
}

function Linha({ rotulo, children, nota }: { rotulo: string; children: React.ReactNode; nota?: string | null }) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <span className="shrink-0 text-[11px] text-ink-400">{rotulo}</span>
      <span className="text-right text-xs text-ink-100">
        {children}
        {nota && <span className="block text-[10px] text-ink-400">{nota}</span>}
      </span>
    </div>
  );
}

export function RiverPopup({ dado }: { dado: RioFeature }) {
  // A ficha lê a estação da store, e não do clique: se os dados se renovam com
  // a ficha aberta, a série nova tem de vir com o `t0` novo — a série velha
  // sobre o `t0` novo aparece deslocada uma hora (o mesmo cuidado de `layer.ts`).
  const cod = dado.rio.estacao.cod;
  const rio = useRiverStore((s) => s.dados.rios.find((r) => r.estacao.cod === cod)) ?? dado.rio;
  const t0 = useRiverStore((s) => s.dados.t0);
  const passoMin = useRiverStore((s) => s.dados.passoMin);

  const displayedTime = useTimeStore((s) => s.displayedTime);
  const currentTime = useTimeStore((s) => s.currentTime);
  const instante = displayedTime ?? currentTime;

  // A cota mostrada é a última MEDIDA até o quadro exibido, sempre com o
  // horário dela: acompanha o player (a ficha aberta durante a animação não
  // fica parada no clique), e numa hora sem medição — o quadro mais recente do
  // satélite costuma ser mais novo que a última leitura da ANA — mostra a
  // anterior em vez de um aviso. Antes da série, a leitura mais recente.
  const medida = useMemo(() => ultimaMedicao(rio.serie, t0, passoMin, instante), [rio.serie, t0, passoMin, instante]);
  const cm = medida?.cm ?? rio.cm;
  const quando = medida?.ms ?? rio.ms;

  const cor = rio.classe ? COR_CLASSE[rio.classe] : COR_SEM_REFERENCIA;
  const frase = useMemo(
    () => fraseComparativa(rio.percentil, rio.referencia, rio.extremos?.desde ?? null),
    [rio.percentil, rio.referencia, rio.extremos],
  );

  return (
    <div className="space-y-1.5">
      <div className="flex items-center gap-1.5 text-xs text-ink-400">
        <span className="inline-block size-2 rounded-full" style={{ background: cor }} aria-hidden />
        Nível do rio · ANA
      </div>

      <div className="leading-tight font-semibold text-white">
        {rio.estacao.nome}
        {rio.classe && <div className="text-xs font-normal" style={{ color: cor }}>{NOME_CLASSE[rio.classe]}</div>}
      </div>

      <div>
        <div className="flex items-baseline gap-2 pt-0.5">
          <span className="text-xl font-semibold tabular-nums" style={{ color: cor }}>
            {cotaM(cm)} m
          </span>
          {rio.tendencia !== 'desconhecida' && (
            <span className="text-xs" style={{ color: COR_TENDENCIA[rio.tendencia] }}>
              {SETA_TENDENCIA[rio.tendencia]} {NOME_TENDENCIA[rio.tendencia]}
            </span>
          )}
        </div>
        {quando > 0 && <div className="text-[10px] text-ink-300 tabular-nums">medido {formatDayTimeBrt(quando)} (Brasília)</div>}
        <div className="text-[10px] text-ink-400">cota da régua local · o zero é uma referência da estação</div>
      </div>

      {/* A vazão é a única grandeza fisicamente comparável entre estações. */}
      {vazaoTexto(rio.vazao) && (
        <div className="rounded-lg bg-white/5 px-2 py-1.5">
          <div className="text-[10px] text-ink-400">Vazão</div>
          <div className="text-sm font-semibold text-ink-100 tabular-nums">{vazaoTexto(rio.vazao)}</div>
        </div>
      )}

      <div className="pt-1">
        <Serie serie={rio.serie} t0={t0} passoMin={passoMin} instante={instante} cor={cor} />
      </div>

      <div className="space-y-1 border-t border-white/10 pt-1.5">
        {variacaoTexto(rio.delta24h, 24) && <Linha rotulo="Em 24 h">{variacaoTexto(rio.delta24h, 24)}</Linha>}
        {variacaoTexto(rio.delta48h, 48) && <Linha rotulo="Em 48 h">{variacaoTexto(rio.delta48h, 48)}</Linha>}

        {rio.referencia ? (
          <>
            <Linha rotulo="Normal desta época" nota={anomaliaTexto(rio.anomaliaCm)}>
              {cotaM(rio.referencia.p50)} m
            </Linha>
            {rio.extremos && (
              <>
                <Linha rotulo="Maior medida" nota={formatDateBrt(Date.parse(`${rio.extremos.mxd}T12:00:00Z`))}>
                  {cotaM(rio.extremos.mx)} m
                </Linha>
                <Linha rotulo="Menor medida" nota={formatDateBrt(Date.parse(`${rio.extremos.mnd}T12:00:00Z`))}>
                  {cotaM(rio.extremos.mn)} m
                </Linha>
              </>
            )}
          </>
        ) : (
          <div className="rounded-lg bg-white/5 px-2 py-1.5 text-[10px] leading-snug text-ink-400">
            Esta estação não tem anos medidos suficientes para dizer o que é normal aqui. A cota está medida; a
            comparação, não.
          </div>
        )}

        {rio.situacao && (
          <Linha rotulo="Situação (SGB)" nota="classificação do Serviço Geológico — não é alerta oficial">
            {rio.situacao}
          </Linha>
        )}
      </div>

      {frase && (
        <div className="rounded-lg bg-white/5 px-2 py-1.5 text-[11px] leading-snug text-ink-200">
          {frase}
          {rio.extremos && (
            <span className="block text-ink-400">
              Extremos desta estação, no período medido desde {rio.extremos.desde} — não o recorde da régua, que é mais
              antigo.
            </span>
          )}
        </div>
      )}

      {rio.bruta && (
        <div className="text-[10px] text-alerta-300">
          Leitura ainda não validada pela ANA (dado bruto do sensor).
        </div>
      )}
      {rio.herdada && (
        <div className="text-[10px] text-ink-400">
          A estação não respondeu na última rodada; esta é a leitura anterior.
        </div>
      )}

      {/* A previsão deste lugar (o "Próximos dias"). */}
      <ProximosDiasAqui lon={rio.estacao.lon} lat={rio.estacao.lat} />
      <div className="border-t border-white/10 pt-1 text-[11px] text-ink-400">
        {formatLonLat(rio.estacao.lon, rio.estacao.lat)} · código {rio.estacao.cod}
        {rio.estacao.operadora && <> · régua operada por {rio.estacao.operadora}</>}
      </div>
    </div>
  );
}
